import React, { useState, useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import { Settings, X, HardDrive, Globe, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';

const SystemSettingsModal = ({ isOpen, onClose }) => {
  const { users, updateStorageQuota, dnsSettings, updateDnsSettings, getUserStorageUsage } = useContext(AppContext);

  // States
  const [selectedUserId, setSelectedUserId] = useState(users[0]?.UserID || '');
  const [newQuota, setNewQuota] = useState('10');
  const [domain, setDomain] = useState(dnsSettings.Domain);
  const [mxRecord, setMxRecord] = useState(dnsSettings.MxRecord);
  const [spfRecord, setSpfRecord] = useState(dnsSettings.SpfRecord);
  
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifySuccess, setVerifySuccess] = useState(dnsSettings.IsVerified);

  if (!isOpen) return null;

  // Handle quota update
  const handleQuotaSubmit = (e) => {
    e.preventDefault();
    if (!selectedUserId || !newQuota) {
      alert('사용자 및 할당 용량을 설정해 주세요.');
      return;
    }
    updateStorageQuota(selectedUserId, newQuota);
    alert('해당 계정의 메일 저장소 할당 용량이 정상적으로 업데이트되었습니다.');
  };

  // Handle DNS Verification
  const handleVerifyDns = () => {
    setIsVerifying(true);
    setTimeout(() => {
      setIsVerifying(false);
      setVerifySuccess(true);
      updateDnsSettings({
        Domain: domain,
        MxRecord: mxRecord,
        SpfRecord: spfRecord,
        IsVerified: true
      });
      alert('메일 서버 DNS 세팅이 완료되었습니다. MX 레코드 및 SPF 정책 검증(Verified)이 정상 종료되었습니다.');
    }, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex justify-center items-center p-4">
      <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-4xl max-h-[85vh] overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col text-left">
        {/* Modal Header */}
        <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <Settings className="w-5 h-5 text-indigo-600" />
            <h2 className="text-base font-bold text-slate-900">시스템 전체 설정 & 메일 서버 연동</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 cursor-pointer p-1 rounded-lg hover:bg-slate-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Scroll Content */}
        <div className="p-6 overflow-y-auto space-y-8 flex-1">
          
          {/* Section 1: Quota Storage Allocation */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* List panel */}
            <div className="lg:col-span-2 space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                <HardDrive className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">계정별 메일 용량 할당 현황</span>
              </div>
              
              <div className="overflow-x-auto border border-slate-200 rounded-xl bg-slate-50 max-h-[220px] overflow-y-auto">
                <table className="w-full text-left text-xs text-slate-700">
                  <thead className="bg-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="px-3 py-2">이름</th>
                      <th className="px-3 py-2">부서/직급</th>
                      <th className="px-3 py-2">용량 사용률 (사용/할당)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                     {users.map(u => {
                      const storageInfo = getUserStorageUsage ? getUserStorageUsage(u.UserID) : { usedMB: 0.1, quotaGB: u.StorageQuota || 10, usagePercent: 0.1 };
                      const quota = storageInfo.quotaGB;
                      const usedLabel = `${storageInfo.usedMB} MB`;
                      const ratio = storageInfo.usagePercent;
                      return (
                        <tr key={u.UserID} className="hover:bg-white transition-colors">
                          <td className="px-3 py-2 font-bold text-slate-900">{u.Name}</td>
                          <td className="px-3 py-2 text-slate-500">{u.Department} • {u.JobTitle}</td>
                          <td className="px-3 py-2 space-y-1">
                            <div className="flex justify-between text-[9px] text-slate-500 font-semibold">
                              <span>{usedLabel} / {quota} GB</span>
                              <span className={ratio > 80 ? 'text-rose-600 font-bold' : 'text-slate-600'}>{ratio.toFixed(1)}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-1">
                              <div
                                className={`h-1 rounded-full ${ratio > 80 ? 'bg-rose-500' : 'bg-indigo-600'}`}
                                style={{ width: `${ratio}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Adjust Quota form */}
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl flex flex-col justify-between space-y-4">
              <div className="space-y-3">
                <span className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider block">스토리지 용량 변경</span>
                
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-slate-600 block uppercase">대상 임직원</label>
                  <select
                    value={selectedUserId}
                    onChange={e => setSelectedUserId(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none"
                  >
                    {users.map(u => (
                      <option key={u.UserID} value={u.UserID}>{u.Name} ({u.Email})</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-slate-600 block uppercase">새 할당 용량 (GB)</label>
                  <select
                    value={newQuota}
                    onChange={e => setNewQuota(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none"
                  >
                    <option value="5">5 GB (일반 부서원)</option>
                    <option value="10">10 GB (표준 권장)</option>
                    <option value="20">20 GB (중요 보관용)</option>
                    <option value="50">50 GB (대용량 보조)</option>
                    <option value="100">100 GB (개발/서버)</option>
                  </select>
                </div>
              </div>

              <button
                onClick={handleQuotaSubmit}
                className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm cursor-pointer transition-colors"
              >
                할당 용량 즉시 적용
              </button>
            </div>
          </div>

          {/* Section 2: Domain/MX/SPF Settings */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
              <Globe className="w-4 h-4 text-indigo-600" />
              <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">사내 메일 서버 DNS 바인딩 세팅 (MX & SPF)</span>
            </div>

            {/* Status alerts */}
            {isVerifying ? (
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 text-center flex items-center justify-center gap-2.5 animate-pulse">
                <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
                <span className="text-xs text-slate-700 font-semibold">SES 메일 게이트웨이 및 DNS 레코드 응답 스캔 중...</span>
              </div>
            ) : verifySuccess ? (
              <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-2xl flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <span className="text-xs font-bold text-emerald-800 block">정상 연결됨 (Verified)</span>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    도메인 <strong>{domain}</strong>의 메일 수신용 MX 레코드와 스팸 필터 우회용 SPF 레코드가 AWS SES 서비스에 성공적으로 바인딩되었습니다.<br />
                    <span className="text-[10px] text-slate-400">(마지막 인증 동기화 일자: {new Date(dnsSettings.LastVerifiedAt).toLocaleString()})</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-2xl flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <span className="text-xs font-bold text-amber-800 block">검증 대기 중 (DNS Record Unverified)</span>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    도메인 설정을 변경했거나 신규 연결 시 하단 레코드 검증이 요구됩니다.
                  </p>
                </div>
              </div>
            )}

            {/* Form grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-5 rounded-2xl border border-slate-200">
              <div className="space-y-1 text-xs">
                <label className="text-[10px] font-bold text-slate-600 block uppercase">연동 회사 도메인 (Domain)</label>
                <input
                  type="text"
                  value={domain}
                  onChange={e => setDomain(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none"
                  placeholder="domain.com"
                />
              </div>

              <div className="space-y-1 text-xs">
                <label className="text-[10px] font-bold text-slate-600 block uppercase">MX Record (수신 라우팅)</label>
                <input
                  type="text"
                  value={mxRecord}
                  onChange={e => setMxRecord(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none font-mono"
                />
              </div>

              <div className="space-y-1 text-xs">
                <label className="text-[10px] font-bold text-slate-600 block uppercase">SPF Record (인증/스팸방지)</label>
                <input
                  type="text"
                  value={spfRecord}
                  onChange={e => setSpfRecord(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none font-mono"
                />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleVerifyDns}
                disabled={isVerifying}
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm cursor-pointer transition-all flex items-center gap-1.5"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isVerifying ? 'animate-spin' : ''}`} />
                DNS 레코드 변경 적용 및 서버 실시간 검증
              </button>
            </div>
          </div>

        </div>

        {/* Modal Footer */}
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl text-xs font-semibold bg-slate-200 hover:bg-slate-300 text-slate-800 transition-colors cursor-pointer"
          >
            설정 닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemSettingsModal;
